import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Settings2 } from "lucide-react"
import { cn } from "@/lib/utils"
import type { BusinessHoursConfig, ResponseAggregation, BusinessHoursScope } from "../../utils/analyticsUtils"

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]

/** Hours as 00:00 … 23:00, plus 24:00 so a window can run to end of day. */
const HOUR_OPTIONS = Array.from({ length: 25 }, (_, h) => ({
  value: String(h),
  label: h === 24 ? "24:00" : `${String(h).padStart(2, "0")}:00`,
}))

/**
 * Common zones first, then whatever the platform supports. `supportedValuesOf`
 * is unavailable in older browsers, so fall back to a short curated list.
 */
function timeZoneOptions(current: string): string[] {
  const common = [
    "UTC", "Africa/Lagos", "Africa/Nairobi", "Africa/Cairo", "Europe/London",
    "Europe/Berlin", "Asia/Beirut", "Asia/Amman", "Asia/Dhaka", "Asia/Manila",
    "America/New_York", "America/Chicago", "America/Los_Angeles",
  ]
  let all: string[] = []
  try {
    all = (Intl as any).supportedValuesOf?.("timeZone") ?? []
  } catch { all = [] }
  const merged = [...new Set([current, ...common, ...all])].filter(Boolean)
  return merged
}

interface Props {
  value: BusinessHoursConfig
  onChange: (cfg: BusinessHoursConfig) => void
  className?: string
}

export function BusinessHoursPopover({ value, onChange, className }: Props) {
  const zones = timeZoneOptions(value.timeZone)

  const toggleDay = (day: number) => {
    const days = value.days.includes(day)
      ? value.days.filter(d => d !== day)
      : [...value.days, day].sort((a, b) => a - b)
    onChange({ ...value, days })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-6 w-6 hover:bg-white/20", className)}
          aria-label="Business hours settings"
        >
          <Settings2 className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Statistic</Label>
            <Select
              value={value.aggregation}
              onValueChange={(v) => onChange({ ...value, aggregation: v as ResponseAggregation })}
            >
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="median" className="text-xs">Median</SelectItem>
                <SelectItem value="average" className="text-xs">Average</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {value.aggregation === "median"
                ? "Typical wait; a few very slow replies won't skew it."
                : "Mean wait; slow outliers pull the number up."}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Count messages</Label>
            <Select
              value={value.scope}
              onValueChange={(v) => onChange({ ...value, scope: v as BusinessHoursScope })}
            >
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All messages</SelectItem>
                <SelectItem value="inside" className="text-xs">Arrived inside business hours</SelectItem>
                <SelectItem value="outside" className="text-xs">Arrived outside business hours</SelectItem>
              </SelectContent>
            </Select>
            {value.scope !== "all" && (
              <p className="text-xs text-muted-foreground">
                Based on when the user's message arrived.
              </p>
            )}
          </div>

          <div className="border-t pt-4 flex items-center justify-between">
            <div>
              <Label htmlFor="bh-enabled" className="text-sm font-medium">Business hours only</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Pause the clock outside working hours
              </p>
            </div>
            <Switch
              id="bh-enabled"
              checked={value.enabled}
              onCheckedChange={(enabled) => onChange({ ...value, enabled })}
            />
          </div>

          <div className={cn("space-y-4", !value.enabled && value.scope === "all" && "opacity-50 pointer-events-none")}>
            <div className="space-y-1.5">
              <Label className="text-xs">Working days</Label>
              <div className="flex gap-1">
                {DAY_LABELS.map((label, day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    aria-label={DAY_NAMES[day]}
                    aria-pressed={value.days.includes(day)}
                    className={cn(
                      "h-8 w-8 rounded-md text-xs font-medium border transition-colors",
                      value.days.includes(day)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background hover:bg-muted border-input"
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">From</Label>
                <Select
                  value={String(value.startHour)}
                  onValueChange={(v) => onChange({ ...value, startHour: Number(v) })}
                >
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {HOUR_OPTIONS.slice(0, 24).map(o => (
                      <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">To</Label>
                <Select
                  value={String(value.endHour)}
                  onValueChange={(v) => onChange({ ...value, endHour: Number(v) })}
                >
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {HOUR_OPTIONS.slice(1).map(o => (
                      <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {value.endHour <= value.startHour && (
              <p className="text-xs text-destructive">
                End time must be after start time.
              </p>
            )}
            {value.days.length === 0 && (
              <p className="text-xs text-destructive">
                Select at least one working day.
              </p>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs">Time zone</Label>
              <Select
                value={value.timeZone}
                onValueChange={(timeZone) => onChange({ ...value, timeZone })}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {zones.map(z => (
                    <SelectItem key={z} value={z} className="text-xs">{z}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
